import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Card } from "@/components/Card";
import { Btn } from "@/components/Btn";
import { ApiError } from "@/api/client";
import { formatDatum } from "@/lib/format";
import { getMailConfig } from "@/api/settings";
import { absenderName, alleAdressen, adressenZusammenfuehren } from "@/lib/mailaddr";
import {
  ComposePayload,
  InboxHeader,
  InboxMail,
  MailFolder,
  attachmentUrl,
  deleteDraft,
  deleteMail,
  getMail,
  listFolders,
  listMessages,
  saveDraft,
  sendMail,
} from "@/api/inbox";

const PAGE_SIZE = 25;

const ROLLE_ICON: Record<string, string> = {
  inbox: "inbox",
  sent: "send",
  drafts: "drafts",
  trash: "delete",
  junk: "report",
  archive: "archive",
  normal: "folder",
};

type ComposeInit = {
  to: string;
  cc: string;
  betreff: string;
  body: string;
  in_reply_to?: string;
  references?: string;
  forward_uid?: string;
  forward_folder?: string;
  draft_uid?: string; // beim Bearbeiten eines Entwurfs
};

export function PosteingangPage() {
  const qc = useQueryClient();
  const [folder, setFolder] = useState<string>("INBOX");
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [compose, setCompose] = useState<ComposeInit | null>(null);

  useEffect(() => {
    const h = window.setTimeout(() => setDebounced(search.trim()), 350);
    return () => window.clearTimeout(h);
  }, [search]);

  // Bei Ordnerwechsel Auswahl zurücksetzen und auf die erste Seite springen.
  useEffect(() => {
    setSelectedUid(null);
    setPage(0);
  }, [folder]);

  // Neue Suche beginnt immer auf Seite 1.
  useEffect(() => {
    setPage(0);
  }, [debounced]);

  const foldersQuery = useQuery({ queryKey: ["mail", "folders"], queryFn: listFolders });
  const mailCfgQuery = useQuery({ queryKey: ["me-mail-settings"], queryFn: getMailConfig });
  const eigeneAdressen = useMemo(() => {
    const c = mailCfgQuery.data;
    return [c?.imap_user, c?.smtp_user].filter((a): a is string => !!a);
  }, [mailCfgQuery.data]);

  const listQuery = useQuery({
    // Eine Mail mehr anfordern als angezeigt, um zu erkennen, ob es eine nächste Seite gibt.
    queryKey: ["mail", "list", folder, debounced, page],
    queryFn: () =>
      listMessages(folder, { limit: PAGE_SIZE + 1, offset: page * PAGE_SIZE, q: debounced }),
    placeholderData: (prev) => prev,
  });

  const mailQuery = useQuery({
    queryKey: ["mail", "msg", folder, selectedUid],
    queryFn: () => getMail(selectedUid as string, folder),
    enabled: !!selectedUid,
  });

  const folders: MailFolder[] = foldersQuery.data ?? [];
  const geladen: InboxHeader[] = listQuery.data ?? [];
  const hatNaechste = geladen.length > PAGE_SIZE;
  const headers: InboxHeader[] = hatNaechste ? geladen.slice(0, PAGE_SIZE) : geladen;
  const aktuellerOrdner = folders.find((f) => f.name === folder);
  const istEntwuerfe = aktuellerOrdner?.rolle === "drafts";
  // In Gesendet/Entwürfe ist der Absender man selbst – dort den Empfänger anzeigen.
  const zeigeEmpfaenger = aktuellerOrdner?.rolle === "sent" || aktuellerOrdner?.rolle === "drafts";

  function refreshList() {
    qc.invalidateQueries({ queryKey: ["mail", "list"] });
  }

  function buildReply(mail: InboxMail, allReply: boolean): ComposeInit {
    const ref = [mail.references, mail.message_id].filter(Boolean).join(" ");
    const empfaenger = adressenZusammenfuehren([alleAdressen(mail.von)], []);
    let cc: string[] = [];
    if (allReply) {
      cc = adressenZusammenfuehren(
        [alleAdressen(mail.an), alleAdressen(mail.cc)],
        [...eigeneAdressen, ...empfaenger],
      );
    }
    const quote = mail.body_text
      ? "\n\n----\n" + mail.body_text.split("\n").map((l) => "> " + l).join("\n")
      : "";
    return {
      to: empfaenger.join(", "),
      cc: cc.join(", "),
      betreff: mail.betreff.toLowerCase().startsWith("re:") ? mail.betreff : `Re: ${mail.betreff}`,
      body: quote,
      in_reply_to: mail.message_id || undefined,
      references: ref || undefined,
    };
  }

  function buildForward(mail: InboxMail): ComposeInit {
    const head =
      `\n\n---------- Weitergeleitete Nachricht ----------\n` +
      `Von: ${mail.von}\nDatum: ${formatDatum(mail.datum)}\nBetreff: ${mail.betreff}\nAn: ${mail.an}\n\n`;
    return {
      to: "",
      cc: "",
      betreff: mail.betreff.toLowerCase().startsWith("fwd:") ? mail.betreff : `Fwd: ${mail.betreff}`,
      body: head + (mail.body_text || ""),
      forward_uid: mail.uid,
      forward_folder: folder,
    };
  }

  function openDraft(mail: InboxMail) {
    setCompose({
      to: mail.an,
      cc: mail.cc,
      betreff: mail.betreff,
      body: mail.body_text,
      draft_uid: mail.uid,
    });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h1 className="display" style={{ fontSize: 22, margin: 0, color: "var(--brand-900)" }}>
          Posteingang
        </h1>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="ghost" icon="refresh" size="sm" onClick={refreshList}>
            Aktualisieren
          </Btn>
          <Btn
            variant="primary"
            icon="edit"
            size="sm"
            onClick={() => setCompose({ to: "", cc: "", betreff: "", body: "" })}
          >
            Verfassen
          </Btn>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "200px minmax(260px, 360px) 1fr",
          gap: 16,
          alignItems: "start",
        }}
      >
        {/* Spalte 1: Ordner */}
        <Card padded={false} style={{ overflow: "hidden" }}>
          <div style={{ maxHeight: "75vh", overflowY: "auto", padding: 6 }}>
            {foldersQuery.isLoading ? (
              <div style={{ padding: 12, color: "var(--fg-muted)", fontSize: 12 }}>Lade Ordner…</div>
            ) : (
              folders.map((f) => (
                <button
                  key={f.name}
                  onClick={() => setFolder(f.name)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "8px 10px",
                    border: "none",
                    borderRadius: 8,
                    background: f.name === folder ? "var(--bg-selected)" : "transparent",
                    color: f.name === folder ? "var(--brand-900)" : "var(--fg-muted)",
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "var(--font-body)",
                    fontSize: 13,
                    fontWeight: f.name === folder ? 600 : 500,
                  }}
                >
                  <span className="material-icons-outlined" style={{ fontSize: 18 }}>
                    {ROLLE_ICON[f.rolle] || "folder"}
                  </span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {f.anzeige_name}
                  </span>
                </button>
              ))
            )}
          </div>
        </Card>

        {/* Spalte 2: Suche + Liste */}
        <Card padded={false} style={{ overflow: "hidden" }}>
          <div style={{ padding: 10, borderBottom: "1px solid var(--border)" }}>
            <div style={{ position: "relative" }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Suchen (Betreff, Absender, Empfänger)…"
                style={{
                  width: "100%",
                  padding: "7px 32px 7px 10px",
                  border: "1px solid var(--border-strong)",
                  borderRadius: 8,
                  fontFamily: "var(--font-body)",
                  fontSize: 13,
                  background: "var(--bg-card)",
                  color: "var(--fg)",
                }}
              />
              {search && (
                <button
                  type="button"
                  title="Suche leeren"
                  aria-label="Suche leeren"
                  onClick={() => setSearch("")}
                  style={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 22,
                    height: 22,
                    border: "none",
                    borderRadius: "50%",
                    background: "transparent",
                    color: "var(--fg-muted)",
                    cursor: "pointer",
                  }}
                >
                  <span className="material-icons-outlined" style={{ fontSize: 16 }}>
                    close
                  </span>
                </button>
              )}
            </div>
          </div>
          {listQuery.isLoading ? (
            <div style={{ padding: 16, color: "var(--fg-muted)", fontSize: 13 }}>Lade…</div>
          ) : listQuery.isError ? (
            <div style={{ padding: 16, color: "var(--error-fg, #991b1b)", fontSize: 13 }}>
              {listQuery.error instanceof ApiError ? listQuery.error.message : "Fehler beim Laden."}
            </div>
          ) : headers.length === 0 ? (
            <div style={{ padding: 16, color: "var(--fg-muted)", fontSize: 13 }}>
              {debounced ? "Keine Treffer." : "Keine Mails."}
            </div>
          ) : (
            <div style={{ maxHeight: "68vh", overflowY: "auto" }}>
              {headers.map((m) => (
                <ListItem
                  key={m.uid}
                  header={m}
                  active={m.uid === selectedUid}
                  zeigeEmpfaenger={zeigeEmpfaenger}
                  onClick={() => setSelectedUid(m.uid)}
                />
              ))}
            </div>
          )}
          {(page > 0 || hatNaechste) && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                borderTop: "1px solid var(--border)",
                fontSize: 12,
                color: "var(--fg-muted)",
              }}
            >
              <Btn
                variant="ghost"
                icon="chevron_left"
                size="sm"
                disabled={page === 0 || listQuery.isFetching}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Zurück
              </Btn>
              <span>Seite {page + 1}</span>
              <Btn
                variant="ghost"
                icon="chevron_right"
                size="sm"
                disabled={!hatNaechste || listQuery.isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Weiter
              </Btn>
            </div>
          )}
        </Card>

        {/* Spalte 3: Lesepane */}
        <Card style={{ minHeight: "60vh" }}>
          {!selectedUid ? (
            <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>Wähle links eine Mail aus.</div>
          ) : mailQuery.isLoading ? (
            <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>Lade Mail…</div>
          ) : mailQuery.isError ? (
            <div style={{ color: "var(--error-fg, #991b1b)", fontSize: 13 }}>
              {mailQuery.error instanceof ApiError ? mailQuery.error.message : "Fehler beim Laden."}
            </div>
          ) : mailQuery.data ? (
            <MailView
              mail={mailQuery.data}
              folder={folder}
              istEntwuerfe={istEntwuerfe}
              onReply={() => setCompose(buildReply(mailQuery.data!, false))}
              onReplyAll={() => setCompose(buildReply(mailQuery.data!, true))}
              onForward={() => setCompose(buildForward(mailQuery.data!))}
              onEditDraft={() => openDraft(mailQuery.data!)}
              onDeleteDraft={async () => {
                await deleteDraft(mailQuery.data!.uid);
                setSelectedUid(null);
                refreshList();
              }}
              onDelete={async () => {
                await deleteMail(mailQuery.data!.uid, folder);
                setSelectedUid(null);
                refreshList();
              }}
            />
          ) : null}
        </Card>
      </div>

      {compose && (
        <ComposeDialog
          initial={compose}
          onClose={() => setCompose(null)}
          onDone={() => {
            setCompose(null);
            refreshList();
          }}
        />
      )}
    </div>
  );
}

function ListItem({
  header,
  active,
  zeigeEmpfaenger,
  onClick,
}: {
  header: InboxHeader;
  active: boolean;
  zeigeEmpfaenger: boolean;
  onClick: () => void;
}) {
  // In Gesendet/Entwürfe den Empfänger (To) statt des Absenders anzeigen.
  const person = zeigeEmpfaenger
    ? header.an
      ? `An: ${absenderName(header.an)}`
      : "(kein Empfänger)"
    : absenderName(header.von);
  return (
    <button
      onClick={onClick}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "10px 14px",
        border: "none",
        borderBottom: "1px solid var(--border)",
        background: active ? "var(--bg-selected)" : "transparent",
        cursor: "pointer",
        fontFamily: "var(--font-body)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <span
          style={{
            fontSize: 13,
            fontWeight: header.gelesen ? 500 : 700,
            color: "var(--fg)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {person}
        </span>
        <span style={{ fontSize: 11, color: "var(--fg-muted)", flexShrink: 0 }}>{formatDatum(header.datum)}</span>
      </div>
      <div
        style={{
          fontSize: 12,
          color: header.gelesen ? "var(--fg-muted)" : "var(--fg)",
          fontWeight: header.gelesen ? 400 : 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          marginTop: 2,
        }}
      >
        {header.hat_anhaenge ? "📎 " : ""}
        {header.betreff || "(kein Betreff)"}
      </div>
    </button>
  );
}

function MailView({
  mail,
  folder,
  istEntwuerfe,
  onReply,
  onReplyAll,
  onForward,
  onEditDraft,
  onDeleteDraft,
  onDelete,
}: {
  mail: InboxMail;
  folder: string;
  istEntwuerfe: boolean;
  onReply: () => void;
  onReplyAll: () => void;
  onForward: () => void;
  onEditDraft: () => void;
  onDeleteDraft: () => void;
  onDelete: () => void;
}) {
  const html = mail.body_html;
  const srcDoc = useMemo(() => {
    if (!html) return null;
    return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"></head><body style="font-family:sans-serif;font-size:14px;color:#222;margin:0;padding:8px">${html}</body></html>`;
  }, [html]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--fg)" }}>{mail.betreff || "(kein Betreff)"}</div>
          <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 4 }}>
            Von: {mail.von} · {formatDatum(mail.datum)}
          </div>
          <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>An: {mail.an}</div>
          {mail.cc ? <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>CC: {mail.cc}</div> : null}
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {istEntwuerfe ? (
            <>
              <Btn variant="secondary" icon="edit" size="sm" onClick={onEditDraft}>
                Bearbeiten
              </Btn>
              <Btn variant="danger" icon="delete" size="sm" onClick={onDeleteDraft}>
                Löschen
              </Btn>
            </>
          ) : (
            <>
              <Btn variant="secondary" icon="reply" size="sm" onClick={onReply}>
                Antworten
              </Btn>
              <Btn variant="ghost" icon="reply_all" size="sm" onClick={onReplyAll}>
                Allen
              </Btn>
              <Btn variant="ghost" icon="forward" size="sm" onClick={onForward}>
                Weiterleiten
              </Btn>
              <Btn variant="danger" icon="delete" size="sm" onClick={onDelete}>
                Löschen
              </Btn>
            </>
          )}
        </div>
      </div>

      {mail.anhaenge.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {mail.anhaenge.map((a) => (
            <a
              key={a.part_id}
              href={attachmentUrl(mail.uid, a.part_id, folder)}
              download={a.dateiname}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                color: "var(--brand-900)",
                textDecoration: "none",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "4px 10px",
                background: "var(--bg-subtle)",
              }}
            >
              <span className="material-icons-outlined" style={{ fontSize: 16 }}>
                attachment
              </span>
              {a.dateiname} ({Math.max(1, Math.round(a.groesse / 1024))} KB)
            </a>
          ))}
        </div>
      )}

      <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12 }}>
        {srcDoc ? (
          <iframe
            title="Mail-Inhalt"
            sandbox=""
            srcDoc={srcDoc}
            style={{ width: "100%", minHeight: "50vh", border: "none", background: "#fff", borderRadius: 8 }}
          />
        ) : (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "var(--font-body)",
              fontSize: 13,
              color: "var(--fg)",
              margin: 0,
            }}
          >
            {mail.body_text || "(kein Inhalt)"}
          </pre>
        )}
      </div>
    </div>
  );
}

const INPUT: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid var(--border-strong)",
  borderRadius: 8,
  fontFamily: "var(--font-body)",
  fontSize: 13,
  background: "var(--bg-card)",
  color: "var(--fg)",
};

function ComposeDialog({
  initial,
  onClose,
  onDone,
}: {
  initial: ComposeInit;
  onClose: () => void;
  onDone: () => void;
}) {
  const [to, setTo] = useState(initial.to);
  const [cc, setCc] = useState(initial.cc);
  const [showCc, setShowCc] = useState(!!initial.cc);
  const [betreff, setBetreff] = useState(initial.betreff);
  const [body, setBody] = useState(initial.body);
  const [markdown, setMarkdown] = useState(false);
  const [preview, setPreview] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const istForward = !!initial.forward_uid;

  function gemeinsamePayload(): ComposePayload {
    return {
      to,
      cc,
      betreff,
      body,
      format: markdown ? "markdown" : "text",
      in_reply_to: initial.in_reply_to,
      references: initial.references,
      forward_uid: initial.forward_uid,
      forward_folder: initial.forward_folder,
      attachments: files,
    };
  }

  const sendMut = useMutation({
    mutationFn: () => sendMail(gemeinsamePayload()),
    onSuccess: onDone,
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : "Versand fehlgeschlagen."),
  });

  const draftMut = useMutation({
    mutationFn: () =>
      saveDraft({
        to,
        cc,
        betreff,
        body,
        format: markdown ? "markdown" : "text",
        replace_uid: initial.draft_uid,
        attachments: files,
      }),
    onSuccess: onDone,
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen."),
  });

  /**
   * Umschließt die aktuelle Auswahl im Textfeld mit ``prefix``/``suffix`` (z. B. **fett**).
   * Ist nichts markiert, wird ein Platzhalter eingefügt und vorausgewählt.
   */
  function formatiere(prefix: string, suffix: string, platzhalter: string) {
    setMarkdown(true);
    const ta = textareaRef.current;
    if (!ta) {
      setBody((b) => b + prefix + platzhalter + suffix);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const aktuell = ta.value;
    const auswahl = aktuell.slice(start, end) || platzhalter;
    const neu = aktuell.slice(0, start) + prefix + auswahl + suffix + aktuell.slice(end);
    setBody(neu);
    // Auswahl nach dem Re-Render auf den umschlossenen Text setzen (Fokus zurück ins Feld).
    const selStart = start + prefix.length;
    const selEnd = selStart + auswahl.length;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(selStart, selEnd);
    });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(24,20,16,.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(720px, 96vw)",
          maxHeight: "92vh",
          overflowY: "auto",
          background: "var(--bg-card)",
          borderRadius: 14,
          boxShadow: "var(--sh-3)",
          border: "1px solid var(--border)",
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>
            {initial.draft_uid ? "Entwurf bearbeiten" : initial.in_reply_to ? "Antworten" : istForward ? "Weiterleiten" : "Neue Mail"}
          </div>
          <Btn variant="ghost" icon="close" size="sm" onClick={onClose} />
        </div>

        <label style={{ fontSize: 12, color: "var(--fg-muted)" }}>
          An (mehrere mit Komma trennen)
          <input style={{ ...INPUT, marginTop: 4 }} value={to} onChange={(e) => setTo(e.target.value)} placeholder="a@x.de, b@y.de" />
        </label>

        {showCc ? (
          <label style={{ fontSize: 12, color: "var(--fg-muted)" }}>
            CC
            <input style={{ ...INPUT, marginTop: 4 }} value={cc} onChange={(e) => setCc(e.target.value)} />
          </label>
        ) : (
          <button
            onClick={() => setShowCc(true)}
            style={{ alignSelf: "flex-start", background: "transparent", border: "none", color: "var(--brand-900)", cursor: "pointer", fontSize: 12 }}
          >
            + CC hinzufügen
          </button>
        )}

        <label style={{ fontSize: 12, color: "var(--fg-muted)" }}>
          Betreff
          <input style={{ ...INPUT, marginTop: 4 }} value={betreff} onChange={(e) => setBetreff(e.target.value)} />
        </label>

        {/* Formatierungs-Toolbar */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <Btn variant="ghost" size="sm" title="Fett" onClick={() => formatiere("**", "**", "fett")}>
            <b>B</b>
          </Btn>
          <Btn variant="ghost" size="sm" title="Kursiv" onClick={() => formatiere("_", "_", "kursiv")}>
            <i>I</i>
          </Btn>
          <Btn variant="ghost" size="sm" icon="format_list_bulleted" title="Liste" onClick={() => formatiere("\n- ", "", "Punkt")} />
          <Btn variant="ghost" size="sm" icon="link" title="Link" onClick={() => formatiere("[", "](https://)", "Text")} />
          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--fg-muted)", marginLeft: 4 }}>
            <input type="checkbox" checked={markdown} onChange={(e) => setMarkdown(e.target.checked)} />
            Formatierung (Markdown)
          </label>
          {markdown && (
            <button
              onClick={() => setPreview((p) => !p)}
              style={{ background: "transparent", border: "none", color: "var(--brand-900)", cursor: "pointer", fontSize: 12 }}
            >
              {preview ? "Bearbeiten" : "Vorschau"}
            </button>
          )}
        </div>

        {markdown && preview ? (
          <div style={{ ...INPUT, minHeight: 180, overflowY: "auto" }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{body || "_(leer)_"}</ReactMarkdown>
          </div>
        ) : (
          <textarea
            ref={textareaRef}
            style={{ ...INPUT, minHeight: 200, resize: "vertical" }}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        )}

        {/* Anhänge */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--brand-900)", cursor: "pointer" }}>
            <span className="material-icons-outlined" style={{ fontSize: 16 }}>
              attach_file
            </span>
            Anhang hinzufügen
            <input
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                const neu = Array.from(e.target.files ?? []);
                setFiles((f) => [...f, ...neu]);
                e.target.value = "";
              }}
            />
          </label>
          {files.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {files.map((f, i) => (
                <span
                  key={i}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: "3px 8px",
                    background: "var(--bg-subtle)",
                  }}
                >
                  {f.name}
                  <button
                    onClick={() => setFiles((arr) => arr.filter((_, idx) => idx !== i))}
                    style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--fg-muted)" }}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}
          {istForward && (
            <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>Original-Anhänge werden automatisch mitgesendet.</div>
          )}
        </div>

        {error && <div style={{ fontSize: 12, color: "var(--error-fg, #991b1b)" }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <Btn
            variant="ghost"
            icon="drafts"
            size="sm"
            onClick={() => draftMut.mutate()}
            disabled={draftMut.isPending}
          >
            Als Entwurf speichern
          </Btn>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="ghost" size="sm" onClick={onClose}>
              Abbrechen
            </Btn>
            <Btn
              variant="primary"
              icon="send"
              size="sm"
              onClick={() => sendMut.mutate()}
              disabled={sendMut.isPending || !to.trim()}
            >
              Senden
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
